import {
  advanceTank,
  emptyInput,
  PLAYER_IDS,
  type GameState,
  type InputState,
  type PlayerId,
  type Players,
  type TankState,
} from "@tanks/game-core";
import { movementToInput } from "@tanks/protocol";

interface DisplayTank {
  x: number;
  y: number;
  angle: number;
}

function angleDelta(from: number, to: number): number {
  let delta = (to - from) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
}

function lerp(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

function seedNumber(seed: string): number {
  let value = 0;
  for (let index = 0; index < seed.length; index += 1) {
    value = Math.imul(value ^ seed.charCodeAt(index), 16_777_619);
  }
  return value >>> 0;
}

export class BattlefieldRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly resizeObserver: ResizeObserver;
  private state: GameState | null = null;
  private playerId: PlayerId | null = null;
  private movement = 0;
  private axisX = 0;
  private axisY = 0;
  private display: Players<DisplayTank> | null = null;
  private animationFrame = 0;
  private lastFrameAt = performance.now();
  private canvasWidth = 0;
  private canvasHeight = 0;

  constructor(canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas 2D is unavailable");
    this.canvas = canvas;
    this.context = context;
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
    this.frame = this.frame.bind(this);
    this.animationFrame = requestAnimationFrame(this.frame);
  }

  setPlayer(playerId: PlayerId): void {
    this.playerId = playerId;
  }

  setMovement(movement: number, axisX = 0, axisY = 0): void {
    this.movement = movement;
    this.axisX = axisX;
    this.axisY = axisY;
  }

  setState(state: GameState): void {
    this.state = state;
    if (!this.display) {
      this.display = {
        1: {
          x: state.tanks[1].x,
          y: state.tanks[1].y,
          angle: state.tanks[1].angle,
        },
        2: {
          x: state.tanks[2].x,
          y: state.tanks[2].y,
          angle: state.tanks[2].angle,
        },
      };
      return;
    }
    for (const id of [1, 2] as const) {
      const target = state.tanks[id];
      const shown = this.display[id];
      const distance = Math.hypot(target.x - shown.x, target.y - shown.y);
      const amount = distance > 100 ? 1 : id === this.playerId ? 0.38 : 0.18;
      shown.x = lerp(shown.x, target.x, amount);
      shown.y = lerp(shown.y, target.y, amount);
      shown.angle += angleDelta(shown.angle, target.angle) * amount;
    }
  }

  private resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.canvasWidth = Math.max(1, Math.round(rect.width * ratio));
    this.canvasHeight = Math.max(1, Math.round(rect.height * ratio));
    if (
      this.canvas.width !== this.canvasWidth ||
      this.canvas.height !== this.canvasHeight
    ) {
      this.canvas.width = this.canvasWidth;
      this.canvas.height = this.canvasHeight;
    }
  }

  private frame(now: number): void {
    const delta = Math.min(0.05, Math.max(0, (now - this.lastFrameAt) / 1_000));
    this.lastFrameAt = now;
    this.predictLocal(delta);
    this.render(now);
    this.animationFrame = requestAnimationFrame(this.frame);
  }

  private predictLocal(delta: number): void {
    if (
      !this.state ||
      !this.display ||
      !this.playerId ||
      this.state.phase !== "playing" ||
      this.movement === 0
    ) {
      return;
    }
    const id = this.playerId;
    const otherId = id === 1 ? 2 : 1;
    const shown = this.display[id];
    const tank: TankState = {
      ...this.state.tanks[id],
      x: shown.x,
      y: shown.y,
      angle: shown.angle,
    };
    const otherTank: TankState = {
      ...this.state.tanks[otherId],
      ...this.display[otherId],
    };
    const movement = movementToInput(this.movement);
    const input: InputState = {
      ...emptyInput(),
      ...movement,
      axisX: this.axisX,
      axisY: this.axisY,
    };
    advanceTank(tank, input, this.state.arena, otherTank, delta, Date.now());
    shown.x = tank.x;
    shown.y = tank.y;
    shown.angle = tank.angle;
  }

  private render(now: number): void {
    const context = this.context;
    context.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    if (!this.state || !this.display) return;
    const arena = this.state.arena;
    const scale = Math.min(
      this.canvasWidth / arena.width,
      this.canvasHeight / arena.height,
    );
    const offsetX = (this.canvasWidth - arena.width * scale) / 2;
    const offsetY = (this.canvasHeight - arena.height * scale) / 2;
    context.save();
    context.translate(offsetX, offsetY);
    context.scale(scale, scale);
    this.drawField(context, this.state);
    for (const wall of arena.walls) {
      this.drawWall(context, wall.x, wall.y, wall.width, wall.height);
    }
    if (this.state.flags) {
      for (const flagOwner of PLAYER_IDS) {
        const flag = this.state.flags[flagOwner];
        const position = flag.carrier === null
          ? flag
          : this.display[flag.carrier];
        this.drawFlag(
          context,
          flag.owner,
          position.x,
          position.y,
          flag.carrier !== null,
        );
      }
    }
    for (const projectile of Object.values(this.state.projectiles)) {
      if (projectile) this.drawProjectile(context, projectile.x, projectile.y);
    }
    this.drawTank(context, this.state.tanks[1], this.display[1], now);
    this.drawTank(context, this.state.tanks[2], this.display[2], now);
    context.restore();
  }

  private drawField(context: CanvasRenderingContext2D, state: GameState): void {
    const { width, height, seed } = state.arena;
    const gradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#263a2b");
    gradient.addColorStop(0.5, "#1d3024");
    gradient.addColorStop(1, "#233526");
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);

    context.strokeStyle = "rgba(212, 224, 185, 0.035)";
    context.lineWidth = 1;
    for (let x = 0; x <= width; x += 50) {
      context.beginPath();
      context.moveTo(x, 0);
      context.lineTo(x, height);
      context.stroke();
    }
    for (let y = 0; y <= height; y += 50) {
      context.beginPath();
      context.moveTo(0, y);
      context.lineTo(width, y);
      context.stroke();
    }

    let random = seedNumber(seed);
    for (let index = 0; index < 70; index += 1) {
      random = Math.imul(random + 0x6d2b79f5, 1 | random);
      const x = (random >>> 0) % width;
      random ^= random >>> 13;
      const y = (random >>> 0) % height;
      const radius = 8 + ((random >>> 5) % 26);
      context.fillStyle =
        index % 2 === 0
          ? "rgba(8, 18, 11, 0.07)"
          : "rgba(201, 218, 171, 0.025)";
      context.beginPath();
      context.arc(x, y, radius, 0, Math.PI * 2);
      context.fill();
    }

    context.strokeStyle = "rgba(201, 218, 171, 0.18)";
    context.lineWidth = 7;
    context.strokeRect(3.5, 3.5, width - 7, height - 7);
  }

  private drawWall(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
  ): void {
    context.save();
    context.shadowColor = "rgba(0, 0, 0, 0.45)";
    context.shadowBlur = 14;
    context.shadowOffsetY = 8;
    const gradient = context.createLinearGradient(x, y, x, y + height);
    gradient.addColorStop(0, "#6e715f");
    gradient.addColorStop(1, "#424b3f");
    context.fillStyle = gradient;
    context.fillRect(x, y, width, height);
    context.shadowColor = "transparent";
    context.strokeStyle = "rgba(229, 220, 184, 0.22)";
    context.lineWidth = 3;
    context.strokeRect(x + 1.5, y + 1.5, width - 3, height - 3);
    context.strokeStyle = "rgba(20, 25, 20, 0.22)";
    context.lineWidth = 1;
    for (let line = 18; line < height; line += 22) {
      context.beginPath();
      context.moveTo(x, y + line);
      context.lineTo(x + width, y + line);
      context.stroke();
    }
    context.restore();
  }

  private drawProjectile(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
  ): void {
    const glow = context.createRadialGradient(x, y, 1, x, y, 16);
    glow.addColorStop(0, "rgba(255, 244, 176, 1)");
    glow.addColorStop(0.3, "rgba(255, 168, 70, 0.9)");
    glow.addColorStop(1, "rgba(255, 108, 40, 0)");
    context.fillStyle = glow;
    context.beginPath();
    context.arc(x, y, 16, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#fff5b6";
    context.beginPath();
    context.arc(x, y, 5, 0, Math.PI * 2);
    context.fill();
  }

  private drawFlag(
    context: CanvasRenderingContext2D,
    owner: PlayerId,
    x: number,
    y: number,
    carried: boolean,
  ): void {
    const color = owner === 1 ? "#e2a640" : "#53b6c4";
    const flagY = carried ? y - 48 : y;
    context.save();
    context.strokeStyle = "rgba(238, 240, 220, 0.72)";
    context.lineWidth = 3;
    context.beginPath();
    context.moveTo(x, flagY + 20);
    context.lineTo(x, flagY - 20);
    context.stroke();
    context.fillStyle = color;
    context.beginPath();
    context.moveTo(x, flagY - 19);
    context.lineTo(x + 22, flagY - 12);
    context.lineTo(x, flagY - 4);
    context.closePath();
    context.fill();
    if (!carried) {
      context.fillStyle = "rgba(238, 240, 220, 0.18)";
      context.beginPath();
      context.arc(x, y, 28, 0, Math.PI * 2);
      context.fill();
    }
    context.restore();
  }

  private drawTank(
    context: CanvasRenderingContext2D,
    tank: TankState,
    display: DisplayTank,
    now: number,
  ): void {
    const isOne = tank.playerId === 1;
    context.save();
    context.translate(display.x, display.y);
    context.rotate(display.angle);
    context.shadowColor = "rgba(0, 0, 0, 0.55)";
    context.shadowBlur = 10;
    context.shadowOffsetY = 6;
    context.fillStyle = "#161b17";
    context.fillRect(-25, -24, 50, 11);
    context.fillRect(-25, 13, 50, 11);
    context.shadowColor = "transparent";
    const body = context.createLinearGradient(-22, -20, 22, 20);
    body.addColorStop(0, isOne ? "#d9a441" : "#5fb8c5");
    body.addColorStop(1, isOne ? "#9a6126" : "#287788");
    context.fillStyle = body;
    context.beginPath();
    context.roundRect(-23, -18, 46, 36, 7);
    context.fill();
    context.fillStyle = isOne ? "#f2bd56" : "#80d2dd";
    context.beginPath();
    context.arc(0, 0, 12, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#202a22";
    context.fillRect(0, -4, 39, 8);
    context.fillStyle = "#dfe5cf";
    context.fillRect(31, -3, 10, 6);
    context.restore();

    if (tank.invulnerableUntil > Date.now()) {
      context.save();
      context.strokeStyle = `rgba(151, 221, 255, ${
        0.5 + Math.sin(now / 110) * 0.2
      })`;
      context.lineWidth = 4;
      context.beginPath();
      context.arc(display.x, display.y, 35, 0, Math.PI * 2);
      context.stroke();
      context.restore();
    }
  }

  destroy(): void {
    cancelAnimationFrame(this.animationFrame);
    this.resizeObserver.disconnect();
  }
}
